import React, { useContext, useEffect, useState } from "react";
import {
  ContainerDiv,
  Footer,
  LoadingIcon,
  Sidebar,
} from "../../../components";
import dayjs from "dayjs";
import { useLocalStorage } from "../../../hooks";
import { DatePicker, message, Select } from "antd";
import { UserContext } from "../../../context/UserContext";
import { getFlightLogs, getOrgs, getSites } from "../../../utils/api";
import LogTable from "./LogTable";
import { UI_DATE_FORMAT } from "../../../consts";
import { useSearchParams } from "react-router-dom";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";

const Logs = () => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "logDropdowns",
    null,
  );

  const { userData, enableTour } = useContext(UserContext);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgData, setOrgData] = useState([]);

  const [currentSite, setCurrentSite] = useState(null);
  const [siteData, setSiteData] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();

  // For date range
  const [startDate, setStartDate] = useState(() => {
    const existingParams = new URLSearchParams(searchParams);
    const startDateFromUrl = existingParams.get("startDate");
    if (startDateFromUrl) {
      return startDateFromUrl;
    } else {
      let today = new Date();
      today.setDate(today.getDate() - 3);
      const yyyy = today.getFullYear();
      const mm = `${today.getMonth() + 1}`.padStart(2, "0");
      const dd = `${today.getDate()}`.padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  });

  const [endDate, setEndDate] = useState(() => {
    const existingParams = new URLSearchParams(searchParams);
    const endDateFromUrl = existingParams.get("endDate");
    if (endDateFromUrl) {
      return endDateFromUrl;
    } else {
      let today = new Date();
      const yyyy = today.getFullYear();
      const mm = `${today.getMonth() + 1}`.padStart(2, "0");
      const dd = `${today.getDate()}`.padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  });

  const [settingDefaults, setSettingDefaults] = useState({});

  // ---------------------------
  //  API Calls
  // ---------------------------
  const fetchOrgs = async () => {
    try {
      const res = await getOrgs();
      const filtered = res.data.data.filter(
        (o) => o.is_active && o.is_pollination,
      );
      const mapped = filtered.map((o) => ({ name: o.name, id: o.id }));
      setOrgData(mapped);
    } catch (err) {
      message.error("Error fetching Orgs!");
    }
  };

  const fetchSites = async (orgId) => {
    try {
      const res = await getSites(orgId);
      const filtered = res.data.data.filter((s) => s.is_active);
      const mapped = filtered.map((s) => ({
        name: s.name,
        displayName: s.display_name,
        id: s.id,
        metadata: s.metadata,
      }));
      setSiteData(mapped);
    } catch (err) {
      message.error("Error fetching Sites!");
    }
  };

  const fetchFlightLogs = async (siteId, start, end) => {
    try {
      setDataLoading(true);
      const startIso = `${start}T00:00:00.000Z`;
      const endIso = `${end}T23:59:59.999Z`;
      const res = await getFlightLogs(siteId, startIso, endIso);
      setData(res?.data?.data || []);
    } catch (err) {
      message.error("Error fetching flight logs!");
    } finally {
      setDataLoading(false);
    }
  };

  // ---------------------------
  //  On mount: fetch Orgs or set Org directly
  // ---------------------------
  useEffect(() => {
    if (userData) {
      if (userData.is_superuser) {
        fetchOrgs();
      } else {
        // not superuser => set org from user
        setCurrentOrg(userData.organization_id);

        // also fetch sites for that org
        getSites(userData.organization_id).then((res) => {
          const filtered = res?.data?.data?.filter((s) => s.is_active) || [];
          const mapped = filtered.map((s) => ({
            name: s.name,
            displayName: s.display_name,
            id: s.id,
            metadata: s.metadata,
          }));
          setSiteData(mapped);
          setCurrentSite(mapped[0]?.id || null); // pick first or null
        });
      }
    }
  }, [userData]);

  // If superuser => fetch sites on currentOrg change
  useEffect(() => {
    if (currentOrg && userData?.is_superuser) {
      fetchSites(currentOrg);
    }
  }, [currentOrg]);

  // ---------------------------
  //  Once orgData arrives => default org from URL param or localStorage
  // ---------------------------
  useEffect(() => {
    if (orgData?.length) {
      const params = new URLSearchParams(searchParams);
      const orgIdFromUrl = params.get("org");
      if (orgIdFromUrl) {
        const foundOrg = orgData.find((o) => o.id === orgIdFromUrl);
        setCurrentOrg(
          foundOrg ? foundOrg.id : defaultDropdown?.org || orgData[0].id,
        );
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else if (!settingDefaults.org) {
        setCurrentOrg(defaultDropdown?.org || orgData[0].id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else {
        setCurrentOrg(orgData[0].id);
      }
    }
  }, [orgData]);

  // ---------------------------
  //  Once siteData arrives => default site from param or localStorage
  // ---------------------------
  useEffect(() => {
    if (siteData?.length) {
      const params = new URLSearchParams(searchParams);
      const siteIdFromUrl = params.get("site");
      if (siteIdFromUrl) {
        const foundSite = siteData.find((s) => s.id === siteIdFromUrl);
        setCurrentSite(foundSite ? foundSite.id : siteData[0].id);
      } else if (!settingDefaults.site) {
        // from localStorage
        const storedSiteId = defaultDropdown?.[currentOrg]?.site; // ID
        if (storedSiteId) {
          const found = siteData.find((s) => s.id === storedSiteId);
          setCurrentSite(found ? found.id : siteData[0].id);
        } else {
          setCurrentSite(siteData[0].id);
        }
        setSettingDefaults((prev) => ({ ...prev, site: true }));
      } else {
        setCurrentSite(siteData[0].id);
      }
    } else {
      setCurrentSite(null);
    }
  }, [siteData]);

  // ---------------------------
  //  Whenever currentOrg or currentSite changes => sync to localStorage + URL
  // ---------------------------
  useEffect(() => {
    if (currentOrg) {
      const params = new URLSearchParams(searchParams);
      params.set("org", currentOrg);
      if (currentSite) {
        params.set("site", currentSite);
      }
      setSearchParams(params);

      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        org: currentOrg,
        [currentOrg]: {
          ...orgObj,
          site: currentSite,
        },
      });
    }
  }, [currentOrg, currentSite]);

  // ---------------------------
  //  Whenever site, date range changes => fetch flight logs
  // ---------------------------
  useEffect(() => {
    if (currentSite && startDate && endDate) {
      fetchFlightLogs(currentSite, startDate, endDate);
    } else {
      setData([]);
    }
  }, [currentSite, startDate, endDate]);

  // ---------------------------
  //  Date range logic
  // ---------------------------
  const disabledDate = (current) => current && current > dayjs().endOf("day");

  const handleDateChange = (dates) => {
    // array of dayjs
    const [start, end] = dates;
    const startStr = start.format(dateFormat);
    const endStr = end.format(dateFormat);
    setStartDate(startStr);
    setEndDate(endStr);

    const params = new URLSearchParams(searchParams);
    params.set("startDate", startStr);
    params.set("endDate", endStr);
    setSearchParams(params);
  };

  return (
    <ContainerDiv>
      <div id="logs">
        <Sidebar active="logs" open={sidebarOpen} setOpen={setSidebarOpen} />
        <div
          className={`flex flex-col w-full px-20 ${
            sidebarOpen ? "pl-60" : ""
          } py-10 md:px-20 justify-center transition-padding ease-in-out duration-200`}
        >
          <div className="w-full flex items-center mb-10 sm:flex-col dropdowns">
            {userData?.is_superuser && (
              <Select
                value={currentOrg ?? undefined}
                className="max-w-32 min-w-[200px] sm:mb-5 mr-2"
                placeholder="Select Organization"
                onChange={setCurrentOrg} // ID
                options={orgData.map((org) => ({
                  value: org.id,
                  label: org.name,
                }))}
              />
            )}
            <Select
              value={currentSite ?? undefined}
              className="max-w-32 min-w-[200px] sm:mb-5 mr-2"
              placeholder="Select Site"
              onChange={(val) => setCurrentSite(val)}
              options={siteData?.map((site) => ({
                value: site.id,
                label: site.displayName,
              }))}
            />
            <RangePicker
              disabledDate={disabledDate}
              defaultValue={[
                dayjs(startDate, dateFormat),
                dayjs(endDate, dateFormat),
              ]}
              format={UI_DATE_FORMAT}
              onChange={handleDateChange}
            />
          </div>
          <p className="w-full text-center font-IBMPlexSansBold text-2xl mb-5 heading">
            Flight Logs
          </p>
          {dataLoading ? (
            <LoadingIcon height="200px" />
          ) : (
            <LogTable data={data} />
          )}
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </ContainerDiv>
  );
};

export default Logs;
